/**
 * Illustration set.
 *
 * Personas → DiceBear "notionists-neutral" — hand-drawn Notion-style portraits
 *   that read as premium persona illustrations. Free API, no key needed.
 *   Docs: https://www.dicebear.com/styles/notionists-neutral
 *
 * Metric icons → refined stroke SVGs, currentColor, thick strokes,
 *   readable in both light and dark modes.
 */

import { Users, TrendingUp, TrendingDown, ShieldCheck, Sparkles, Clock, Layers, Zap } from "lucide-react";

// ────────────────────────────────────────────────────────────
// Persona portraits — DiceBear (hand-drawn Notion style)
// ────────────────────────────────────────────────────────────

const dice = (seed, backgroundColor = "transparent") =>
  `https://api.dicebear.com/9.x/notionists-neutral/svg?seed=${encodeURIComponent(
    seed
  )}&backgroundColor=${backgroundColor}&radius=50`;

export const PersonaPortrait = ({ seed, size = 96, alt = "" }) => (
  <img
    src={dice(seed)}
    alt={alt}
    width={size}
    height={size}
    loading="lazy"
    className="w-full h-full object-contain"
    draggable={false}
  />
);

// ────────────────────────────────────────────────────────────
// Illustrated colored puck — used for both personas + icons
// ────────────────────────────────────────────────────────────

const TONE = {
  lime: {
    bg: "bg-[color:var(--nn-accent)]/20 dark:bg-[color:var(--nn-accent)]/20",
    ring: "ring-[color:var(--nn-accent)]/60",
    icon: "text-[color:var(--nn-accent-text)]",
  },
  blue: {
    bg: "bg-[#5aa3e0]/25",
    ring: "ring-[#5aa3e0]/60",
    icon: "text-[#5aa3e0]",
  },
  pink: {
    bg: "bg-[#e07aa0]/25",
    ring: "ring-[#e07aa0]/60",
    icon: "text-[#e07aa0]",
  },
  peach: {
    bg: "bg-[#e08a4d]/25",
    ring: "ring-[#e08a4d]/60",
    icon: "text-[#e08a4d]",
  },
  mint: {
    bg: "bg-[#3eb08a]/25",
    ring: "ring-[#3eb08a]/60",
    icon: "text-[#3eb08a]",
  },
};

export const IllustratedDisc = ({ children, tone = "lime", size = 96 }) => {
  const t = TONE[tone] || TONE.lime;
  return (
    <div
      className={`shrink-0 rounded-full grid place-items-center ring-1 ${t.bg} ${t.ring} ${t.icon}`}
      style={{ width: size, height: size }}
    >
      {children}
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// Metric icons — polished lucide-react at large size
// ────────────────────────────────────────────────────────────

// Icon default stroke = 1.5. We render larger + slightly heavier for premium feel.
const iconProps = { strokeWidth: 1.6, size: 34 };

export const IconTrendUp = (props) => <TrendingUp {...iconProps} {...props} />;
export const IconTrendDown = (props) => <TrendingDown {...iconProps} {...props} />;
export const IconShield = (props) => <ShieldCheck {...iconProps} {...props} />;
export const IconSparkles = (props) => <Sparkles {...iconProps} {...props} />;
export const IconClockLg = (props) => <Clock {...iconProps} {...props} />;
export const IconStackLg = (props) => <Layers {...iconProps} {...props} />;
export const IconUsersLg = (props) => <Users {...iconProps} {...props} />;
export const IconZap = (props) => <Zap {...iconProps} {...props} />;
